import { api } from './client'

/**
 * C-16: client di `RuleLifecycleController` (`/api/rule-lifecycle`), solo Admin e dietro il flag
 * `regole.ciclo_vita`. Un solo ciclo di vita per le famiglie di regole: Bozza -> Test -> Simulazione ->
 * Approvazione -> Pubblicata -> Attiva -> Archiviata.
 */
export const RULE_LIFECYCLE_FLAG = 'regole.ciclo_vita'

export type RuleLifecycleFamily =
  | 'lead-routing' | 'lead-scoring' | 'validation'
  | 'batch-assignment' | 'priority' | 'automation' | 'revenue'
  | 'contest' | 'next-best-action' | 'commission-rates'

export type RuleLifecycleStatus =
  | 'Bozza' | 'Test' | 'Simulazione' | 'Approvazione' | 'Pubblicata' | 'Attiva' | 'Archiviata'

export interface RuleLifecycleRow {
  id: number
  name: string
  isActive: boolean
  status: RuleLifecycleStatus
  version: number
  previousVersionId: number | null
  reason: string | null
  validFrom: string | null
  validTo: string | null
  authorId: number | null
  submittedBy: number | null
  publishedBy: number | null
  publishedAt: string | null
  next: RuleLifecycleStatus[]
  /** R3-29: la regola si simula nelle fasi Test e Simulazione. */
  canSimulate: boolean
  /** R3-29: la famiglia avanza dalla sua pagina (es. listino provvigionale): qui si vede e si simula. */
  managedElsewhere: string | null
}

/** R3-29: una simulazione salvata (serve per chiedere l'approvazione). */
export interface RuleSimulation {
  id: number
  ruleVersion: number
  phase: RuleLifecycleStatus
  from: string
  to: string
  summary: string
  result: unknown
  createdBy: number | null
  createdAt: string
}

export interface RuleLifecycleTransitionRequest {
  to: RuleLifecycleStatus
  reason: string
  validFrom?: string | null
  validTo?: string | null
}

export const ruleLifecycleApi = {
  list: (family: RuleLifecycleFamily) => api<RuleLifecycleRow[]>(`/api/rule-lifecycle/${family}`),
  transition: (family: RuleLifecycleFamily, id: number, body: RuleLifecycleTransitionRequest) =>
    api<RuleLifecycleRow>(`/api/rule-lifecycle/${family}/${id}/transition`, {
      method: 'POST',
      body: JSON.stringify(body),
    }),
  simulate: (family: RuleLifecycleFamily, id: number, period?: { from?: string; to?: string }) =>
    api<RuleSimulation>(`/api/rule-lifecycle/${family}/${id}/simulate`, {
      method: 'POST',
      body: JSON.stringify(period ?? {}),
    }),
  simulations: (family: RuleLifecycleFamily, id: number) =>
    api<RuleSimulation[]>(`/api/rule-lifecycle/${family}/${id}/simulations`),
}

/** R3-29: regole configurabili delle azioni consigliate (`/api/next-best-action-rules`, solo Admin, flag regole.ciclo_vita). */
export interface NextBestActionRule {
  id: number
  name: string
  kind: string
  priority: 'Alta' | 'Media' | 'Bassa' | null
  windowDays: number | null
  urgentDays: number | null
  status: RuleLifecycleStatus
  version: number
  previousVersionId: number | null
  isActive: boolean
}

export interface NextBestActionRuleRequest {
  name: string
  kind?: string
  priority: string | null
  windowDays: number | null
  urgentDays: number | null
}

export const nextBestActionRulesApi = {
  list: () => api<NextBestActionRule[]>('/api/next-best-action-rules'),
  create: (body: NextBestActionRuleRequest) =>
    api<NextBestActionRule>('/api/next-best-action-rules', { method: 'POST', body: JSON.stringify(body) }),
  update: (id: number, body: NextBestActionRuleRequest) =>
    api<NextBestActionRule>(`/api/next-best-action-rules/${id}`, { method: 'PUT', body: JSON.stringify(body) }),
  clone: (id: number) => api<NextBestActionRule>(`/api/next-best-action-rules/${id}/clone`, { method: 'POST' }),
}
